import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import PageHeaderContainer from 'containers/PageHeader';
import {
  STORES_TYPE,
  STORES_GROUPS_TYPE,
  ACCOUNTS_TYPE,
  TAB_TO_QUERY_PARAM,
  GlobalFilterKey,
} from 'utils/types';
import { periodEndWeekSelector } from 'containers/AccessControl/selectors';
import * as globalSelectors from 'store/global/selectors';
import SalesHighlightsWidget from 'components/SalesHighlightsWidget';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import { globalFilterSelector } from 'containers/PageHeader/selectors';
import { getDetailsNavigation, getGroupDetailsNavigation } from 'utils/functions';
import { ROUTES } from 'utils/routes';
import FilterBarContainer from 'containers/PageHeader/FilterBar';
import ReactGA from 'react-ga';
import TableTabs from 'components/TableTabs';
import { Widget, SearchInput } from '@spins/react-ui-library';
import AccountsTable from 'components/Tables/Accounts';
import {
  useTableQueryState,
  useTableQuery,
  QueryResult,
  QueryStateResult,
} from '@spins/react-table';
import { ACCOUNTS_DETAIL_SERIES, STORES_DETAIL_SERIES } from 'apollo/queries';
import StoresTable from 'components/Tables/Stores';
import GroupTable from 'components/GroupTable';
import TrendChartWidget from 'components/Charts/Widgets/TrendChartWidget';
import DistributionChartWidget from 'components/Charts/Widgets/DistributionChartWidget';
import ShareAccountChartWidget from 'components/Charts/Widgets/ShareAccountChartWidget';
import TableFilterTiles from 'components/Filter/TableFilterTiles';

const Accounts = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const clientId: string = useSelector(globalSelectors.clientIdSelector);
  const currentBrand: string = useSelector(globalSelectors.currentBrandSelector);
  const globalFilter = useSelector(globalFilterSelector);
  const periodEndWeek = useSelector(periodEndWeekSelector);
  const isBrandFilterable = useSelector(globalSelectors.isBrandFilterableSelector);
  const [selectedTab, setSelectedTab] = useState(ACCOUNTS_TYPE);
  const [storeGroupsTotal, setStoreGroupsTotal] = useState('0');
  const [storeGroupsSearchText, setStoreGroupsSearchText] = useState('');

  useEffect(() => {
    ReactGA.pageview(ROUTES.ACCOUNTS);
  }, []);

  useEffect(() => {
    if (location.state) {
      // @ts-ignore
      let { weeks, ...rest } = location.state;
      dispatch(pageHeaderActions.updateGlobalFilterArrays(rest, GlobalFilterKey.GLOBAL));
      dispatch(pageHeaderActions.updateGlobalFilterWeeks(weeks));
    }
  }, [dispatch, location.state]);

  // tables state
  const tablesState: QueryStateResult = useTableQueryState([
    {
      id: 'accounts',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter,
      },
    },
    {
      id: 'stores',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter,
      },
    },
  ]);

  // accounts query
  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError,
  }: QueryResult = useTableQuery(ACCOUNTS_DETAIL_SERIES, {
    fetchPolicy: 'cache-first',
    queryState: tablesState.queryState('accounts'),
    searchAttributes: ['name'],
  });

  // stores query
  const {
    data: storesData,
    loading: storesLoading,
    error: storesError,
  }: QueryResult = useTableQuery(STORES_DETAIL_SERIES, {
    fetchPolicy: 'cache-first',
    queryState: tablesState.queryState('stores'),
    searchAttributes: ['name'],
  });

  const tabs = [
    {
      name: ACCOUNTS_TYPE,
      total:
        accountsData && accountsData.accountDetailsSeries
          ? accountsData.accountDetailsSeries.page.totalItems
          : 0,
    },
    {
      name: STORES_TYPE,
      total:
        storesData && storesData.storeDetailsSeries
          ? storesData.storeDetailsSeries.page.totalItems
          : 0,
    },
  ];

  if (isBrandFilterable) {
    tabs.push({
      name: STORES_GROUPS_TYPE,
      total: storeGroupsTotal,
    });
  }

  return (
    <React.Fragment>
      <Row>
        <PageHeaderContainer
          globalFilterContext={GlobalFilterKey.GLOBAL}
          template="brandOverview"
          exportPdfAvailable={true}
        />
      </Row>
      <FilterBarContainer globalFilterContext={GlobalFilterKey.GLOBAL} />
      <Row className="mt-3">
        <Col>
          <SalesHighlightsWidget
            clientId={clientId}
            periodEndWeek={periodEndWeek}
            currentBrand={currentBrand}
            globalFilter={globalFilter}
            type="accounts-items"
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-3">
          <TrendChartWidget globalFilter={globalFilter} />
        </Col>
        <Col md={4} className="mt-3">
          <ShareAccountChartWidget globalFilter={globalFilter} />
        </Col>
        <Col md={4} className="mt-3">
          <DistributionChartWidget globalFilter={globalFilter} />
        </Col>
      </Row>
      {tablesState.sharedState.filtering.filters.length > 0 && (
        <Row className="mt-3">
          <Col>
            <TableFilterTiles filtering={tablesState.sharedState.filtering} />
          </Col>
        </Row>
      )}
      <Row className="mt-3">
        <Col>
          <Widget>
            {{
              header: {
                content: {
                  left: (
                    <TableTabs
                      selectedTab={selectedTab}
                      tabs={tabs}
                      onSelect={(key) => {
                        setSelectedTab(key);
                      }}
                    />
                  ),
                  center: (
                    <SearchInput
                      style={{ width: '300px' }}
                      placeholder="Search..."
                      onChange={(value) => {
                        tablesState.sharedState.searching.search(value);
                        setStoreGroupsSearchText(value);
                      }}
                    />
                  ),
                },
              },
              body: {
                content: (
                  <>
                    {selectedTab === ACCOUNTS_TYPE && (
                      <AccountsTable
                        error={accountsError}
                        queryState={tablesState.queryState('accounts')}
                        rows={
                          accountsData && accountsData.accountDetailsSeries
                            ? accountsData.accountDetailsSeries.data
                            : []
                        }
                        showLoading={accountsLoading}
                        totalRows={
                          accountsData && accountsData.accountDetailsSeries
                            ? accountsData.accountDetailsSeries.page.totalItems
                            : 0
                        }
                        onRowClick={
                          isBrandFilterable &&
                          ((row) => {
                            let entity = TAB_TO_QUERY_PARAM.get(selectedTab); // account, store, etc
                            history.push({
                              ...getDetailsNavigation(row.name, entity, row),
                              state: row,
                            });
                          })
                        }
                      />
                    )}
                    {selectedTab === STORES_TYPE && (
                      <StoresTable
                        error={storesError}
                        queryState={tablesState.queryState('stores')}
                        rows={
                          storesData && storesData.storeDetailsSeries
                            ? storesData.storeDetailsSeries.data
                            : []
                        }
                        showLoading={storesLoading}
                        showTopOpportunity={true}
                        totalRows={
                          storesData && storesData.storeDetailsSeries
                            ? storesData.storeDetailsSeries.page.totalItems
                            : 0
                        }
                        onRowClick={
                          isBrandFilterable &&
                          ((row) => {
                            let entity = TAB_TO_QUERY_PARAM.get(selectedTab); // account, store, etc
                            let storeInfo = row.name.split(',');
                            const name = storeInfo[0] + ', ' + storeInfo[1];
                            history.push({
                              ...getDetailsNavigation(row.storeTag, entity, row, name),
                              state: row,
                            });
                          })
                        }
                      />
                    )}
                    {isBrandFilterable && (
                      // group table query should be executed despite not visible
                      <div
                        style={{ display: selectedTab === STORES_GROUPS_TYPE ? 'block' : 'none' }}
                      >
                        <GroupTable
                          key={selectedTab} // key present to re-render after tab change so onRowClick is updated
                          clientID={clientId}
                          globalFilter={globalFilter}
                          type={STORES_GROUPS_TYPE}
                          search={storeGroupsSearchText}
                          onRowClick={(name, _data, id) => {
                            let entity = TAB_TO_QUERY_PARAM.get(selectedTab); // account, store, etc
                            history.push({
                              ...getGroupDetailsNavigation(name, entity, id),
                            });
                          }}
                          onQueryFinish={(total) => setStoreGroupsTotal(total)}
                        />
                      </div>
                    )}
                  </>
                ),
              },
            }}
          </Widget>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Accounts;
